import {BreadcrumbsTypes} from 'presentation/components/MainHeader/Breadcrumbs/types';
import {HomeEnum, MyHospitalizationFlowEnum} from 'presentation/constants';
import {useContractStatus, useHostName} from 'presentation/hooks';
import {useAccountInformation} from 'presentation/pages/private/hooks';
import {useMyHospitalizationFeatureFlag} from 'presentation/pages/private/hooks/useMyHospitalizationFeatureFlag';
import {translations} from 'presentation/translations-new';
import {useCallback} from 'react';
import {useLocation, useNavigate} from 'react-router-dom';

const {
	pageTitle,
	home: {cards},
} = translations['pt-br'].privateFlow.myHospitalization;

export const useMyHospitalizationLayout = () => {
	const navigate = useNavigate();
	const {pathname, state: routeState} = useLocation();
	const {isResponsibleWithoutBenefits} = useAccountInformation();
	const {isValid} = useContractStatus();
	const {isNotredameSaude} = useHostName();

	const {isEnabled} = useMyHospitalizationFeatureFlag();

	const showMyHospitalizationAlert =
		isValid && !isResponsibleWithoutBenefits && (isEnabled || isNotredameSaude);

	const isHomePath = pathname === MyHospitalizationFlowEnum.HOME;
	const isGeneralOrientationsPath = pathname.includes(
		MyHospitalizationFlowEnum.GENERAL_ORIENTATIONS,
	);
	const isInformationPath = pathname.includes(MyHospitalizationFlowEnum.INFORMATION);
	const isOpinionsPath = pathname.includes(MyHospitalizationFlowEnum.OPINIONS);
	const isPatientDataPath = pathname.includes(MyHospitalizationFlowEnum.PATIENT_DATA);
	const isExamsPath = pathname.includes(MyHospitalizationFlowEnum.EXAMS);
	const isDoctorVisitsPath = pathname.includes(MyHospitalizationFlowEnum.DOCTOR_VISITS);
	const isProceduresPath = pathname.includes(MyHospitalizationFlowEnum.PROCEDURES);
	const isPrescriptionsPath = pathname.includes(MyHospitalizationFlowEnum.PRESCRIPTIONS);

	const renderPageTitle = () => {
		if (isGeneralOrientationsPath) {
			return cards.generalOrientations;
		}
		if (isInformationPath) {
			return cards.informations;
		}
		if (isOpinionsPath) {
			return cards.opinions;
		}
		if (isPatientDataPath) {
			return cards.patientData;
		}
		if (isExamsPath) {
			return cards.exams;
		}
		if (isDoctorVisitsPath) {
			return cards.doctorVisits;
		}
		if (isProceduresPath) {
			return cards.procedures;
		}
		if (isPrescriptionsPath) {
			return cards.prescriptions;
		}

		return pageTitle;
	};

	const renderBreadcrumbs = () => {
		const breadcrumbs: BreadcrumbsTypes['paths'] = [
			{
				title: pageTitle,
				route: MyHospitalizationFlowEnum.HOME,
			},
		];
		if (isGeneralOrientationsPath) {
			breadcrumbs.push({
				title: cards.generalOrientations,
			});
			return breadcrumbs;
		}
		if (isInformationPath) {
			breadcrumbs.push({
				title: cards.informations,
			});
			return breadcrumbs;
		}
		if (isOpinionsPath) {
			breadcrumbs.push({
				title: cards.opinions,
			});
			return breadcrumbs;
		}
		if (isPatientDataPath) {
			breadcrumbs.push({
				title: cards.patientData,
			});
			return breadcrumbs;
		}
		if (isExamsPath) {
			breadcrumbs.push({
				title: cards.exams,
			});
			return breadcrumbs;
		}
		if (isDoctorVisitsPath) {
			breadcrumbs.push({
				title: cards.doctorVisits,
			});
			return breadcrumbs;
		}
		if (isProceduresPath) {
			breadcrumbs.push({
				title: cards.procedures,
			});
			return breadcrumbs;
		}
		if (isPrescriptionsPath) {
			breadcrumbs.push({
				title: cards.prescriptions,
			});
			return breadcrumbs;
		}

		return undefined;
	};

	const handleBack = useCallback(() => {
		if (!isHomePath) {
			navigate(MyHospitalizationFlowEnum.HOME);
			return;
		}
		if (routeState) {
			navigate(routeState);
			return;
		}
		navigate(HomeEnum.HOME_PAGE);
	}, [routeState, isHomePath]);

	return {
		renderPageTitle,
		renderBreadcrumbs,
		handleBack,
		isEnabled: showMyHospitalizationAlert,
	};
};
